import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { DEFAULT_PAGINATION, TableStateFilter } from '@frontend/const'
import { useTablePageSize } from '@frontend/hooks/useTablePageSize'
import { useCurrentUserCompanySetting } from '@frontend/lib/CompanySettingsContext'
import { useLoggedInUserCompanyPermissions } from '@frontend/lib/context/LoggedInUserCompanyPermissionsContext'
import { useLoggedInUserFacilityPermissions } from '@frontend/lib/context/LoggedInUserFacilityPermissionsContext'
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATETIME_FORMAT,
  DEFAULT_ITALIAN_DATE_FORMAT,
  DEFAULT_ITALIAN_DATE_TIME_FORMAT,
  getUtcDayjs,
} from '@frontend/lib/datetime'
import { useLoggedInUser } from '@frontend/lib/LoggedInUserContext'
import { logException } from '@frontend/lib/logger'
import { useMutateWithNotifications } from '@frontend/lib/mutation'
import { useNotify } from '@frontend/lib/notify'
import { hasPermission } from '@frontend/lib/permission'
import { getUTCDateOrDisplayPlaceholder } from '@frontend/lib/text'
import { useMutateGetShipmentSecretToken } from '@frontend/mutations/shipments'
import { useQueryCompanyFacilityDepartments } from '@frontend/queries/departments'
import { useQueryShipment } from '@frontend/queries/shipments'
import { openBookShipmentSider } from '@frontend/stores/CalendarBookingStore'
import { FormInstance, TablePaginationConfig } from 'antd'
import { Dayjs } from 'dayjs'
import { ZodSchema } from 'zod'

import { AnyCompanyPermissionType, NOTIFICATION_STATUS, UserRole } from '@shared/const'
import {
  isUserOwner,
  isUserSupplierOrTransporter,
  isUserTransporter,
} from '@shared/lib/view-context'

import { Identifier, Nullable } from '@shared-types/util'

import { retrieveErrorMessage } from './shared/ErrorCode'
import { CurrentPageOverride } from './BackendPaginatedTable'

export const importFixHack = () => {
  // For some weird reason, we need to use the useMutateWithNotifications hook here in order to make turbopack work
  // It does not necessarily need to be in this function, anywhere in the file would do
  useMutateWithNotifications()
}

export const useIsSupplierOrTransporterView = () => {
  const currentUser = useLoggedInUser()
  return isUserSupplierOrTransporter(currentUser)
}

export const useIsTransporterView = () => {
  const currentUser = useLoggedInUser()
  return isUserTransporter(currentUser)
}

export const useIsOwnerView = () => {
  const currentUser = useLoggedInUser()
  return isUserOwner(currentUser)
}

export const useRefreshSSRProps = () => {
  const router = useRouter()
  return useCallback(() => router.replace(router.asPath, undefined, { scroll: false }), [router])
}

export const useFormGetSet = <T,>(form: FormInstance, fieldName: string) => {
  const setFieldValue = useCallback(
    (value: T) => {
      form.setFieldValue(fieldName, value)
    },
    [form, fieldName]
  )

  const getFieldValue = useCallback(() => form.getFieldValue(fieldName), [form, fieldName])

  return [setFieldValue, getFieldValue] as [(value: T) => void, () => T]
}

export function useDebounce<T>(value: T, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler: NodeJS.Timeout = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Cancel the timeout if value changes (also on delay change or unmount)
    return () => {
      clearTimeout(handler)
    }
    // Depend on the JSON representation of `value`, so that objects/arrays do not
    // trigger a change
  }, [JSON.stringify(value), delay])

  return debouncedValue
}

export function useGoToCreateShipment(params?: { purchaseOrderId: Identifier }) {
  const currentUser = useLoggedInUser()
  const router = useRouter()
  const isSupplierOrTransporter = useIsSupplierOrTransporterView()

  return useCallback(() => {
    if (isSupplierOrTransporter) {
      return router.push('/shipments/create')
    }

    void router.push(`/facilities/${currentUser.currentFacility}/calendar`)
    openBookShipmentSider({
      showCreateShipmentForm: true,
      purchaseOrderId: params?.purchaseOrderId,
    })
  }, [currentUser, isSupplierOrTransporter, params?.purchaseOrderId, router])
}

export function useGetShipmentSelectedTags(shipmentNid?: string) {
  const shipmentQuery = useQueryShipment(shipmentNid)
  const selectedTags = useMemo(
    () => shipmentQuery?.data?.tags.map((tag) => tag.value) || [],
    [shipmentQuery?.data?.tags]
  )
  return selectedTags
}

export const getMainAndSiderSpans = ({ siderWidth }: { siderWidth: number }) => {
  return {
    mainColSpan: 24 - siderWidth,
    siderColSpan: siderWidth,
  }
}

export const useAnchoredPopupContainer = (parentId?: string) => {
  return useMemo(() => {
    if (parentId) {
      return () => document.getElementById(parentId)!
    }
    return () => document.body
  }, [parentId])
}

export function getShipmentTimestampFunctions() {
  const getDayjs = (dateStr: string) => getUtcDayjs(dateStr)

  const getDateOrPlaceholder = (dateStr?: Nullable<string>) =>
    getUTCDateOrDisplayPlaceholder(dateStr)

  const toTimestampPayload = (dateMom?: Nullable<Dayjs>) => {
    if (!dateMom) {
      return null
    }
    return dateMom.utc(true).toISOString()
  }

  return {
    getDayjs,
    getDateOrPlaceholder,
    toTimestampPayload,
  }
}

export const useGetBookingPortalToken = () => {
  const shipmentSecretTokenMutate = useMutateGetShipmentSecretToken()
  const notify = useNotify()

  return useCallback(
    async (nid: string): Promise<Nullable<string>> => {
      try {
        const token = await shipmentSecretTokenMutate.mutateAsync({ shipmentNid: nid })
        return token
      } catch {
        notify({
          message: 'Shipment secret token fetching failed',
          type: NOTIFICATION_STATUS.error,
        })
      }

      return null
    },
    [notify, shipmentSecretTokenMutate]
  )
}

export const useGoToBookingPortal = (successCallback?: () => void) => {
  const router = useRouter()
  const getBookingPortalToken = useGetBookingPortalToken()

  return useCallback(
    async (nid: string) => {
      const token = await getBookingPortalToken(nid)
      if (!token) {
        return
      }

      void router.push(`/bookings/${token}`)
      if (successCallback) {
        successCallback()
      }
    },
    [router, getBookingPortalToken, successCallback]
  )
}

export const useCopyLinkToBookingPortal = () => {
  const getBookingPortalToken = useGetBookingPortalToken()
  const { t } = useTranslation(['bookings'])
  const router = useRouter()
  const notify = useNotify()

  const basePath = useMemo(() => {
    const path = `${location.protocol}//${location.host}`

    if (router.locale) {
      return `${path}/${router.locale}`
    }

    return path
  }, [router])

  return useCallback(
    async (shipmentNid: string) => {
      const token = await getBookingPortalToken(shipmentNid)
      if (token) {
        setTimeout(() => {
          void navigator.clipboard.writeText(`${basePath}/bookings/${token}`)
        }, 0)

        notify({
          message: t('Successfully copied link to booking portal', { ns: 'bookings' }),
          type: NOTIFICATION_STATUS.success,
        })
      }
    },
    [basePath, getBookingPortalToken, notify, t]
  )
}

export const useIsReadOnly = () => {
  const currentUser = useLoggedInUser()
  return !currentUser.roles.includes(UserRole.ADMIN)
}

export const useIsCompanyAdminOrSuperAdmin = () => {
  const currentUser = useLoggedInUser()
  return currentUser.is_company_admin || currentUser.isSuperAdmin
}

export const useIsCompanyAdminOrSuperAdminOrHasPermission = (
  permission: AnyCompanyPermissionType
) => {
  const isCompanyAdminOrSuperAdmin = useIsCompanyAdminOrSuperAdmin()
  const hasManageUserPermission = useHasPermission(permission)

  return isCompanyAdminOrSuperAdmin || hasManageUserPermission
}

export function useResetPaginationHook<T>(
  paginationKey: string,
  dependencies: T[],
  defaultPagination: TablePaginationConfig = DEFAULT_PAGINATION
) {
  const [paginationParams, setPaginationParams] = useSynchronizedPagination(paginationKey, {
    ...defaultPagination,
    showSizeChanger: true,
  })
  const [overrideCurrentPage, setOverrideCurrentPage] = useState<CurrentPageOverride>({})

  useEffect(() => {
    // resets tables to page 1 when there are filter changes and also when facility changes
    setOverrideCurrentPage({ current: 1 })
    setPaginationParams((paginationParams) => ({ ...paginationParams, current: 1 }))
  }, dependencies)

  return { paginationParams, setPaginationParams, overrideCurrentPage }
}

export const useSynchronizedPagination = (
  paginationKey: string,
  defaultPagination: TablePaginationConfig = DEFAULT_PAGINATION
): [TablePaginationConfig, React.Dispatch<React.SetStateAction<TablePaginationConfig>>] => {
  const [pageSize] = useTablePageSize(
    paginationKey,
    defaultPagination.pageSize ?? DEFAULT_PAGINATION.pageSize
  )
  const [localPagination, setLocalPagination] = useState<TablePaginationConfig>({
    ...defaultPagination,
    pageSize,
  })

  const pagination =
    localPagination.pageSize === pageSize
      ? localPagination
      : { ...localPagination, current: 1, pageSize }

  return [pagination, setLocalPagination]
}

export const useDateFormat = (options?: { defaultFormat?: string; italianFormat?: string }) => {
  const router = useRouter()
  const defaultFormat = options?.defaultFormat ?? DEFAULT_DATE_FORMAT
  const italianFormat = options?.italianFormat ?? DEFAULT_ITALIAN_DATE_FORMAT

  if (router.locale === 'it') {
    return italianFormat
  }

  return defaultFormat
}

export const useDateTimeFormat = () => {
  const router = useRouter()
  if (router.locale === 'it') {
    return DEFAULT_ITALIAN_DATE_TIME_FORMAT
  }

  return DEFAULT_DATETIME_FORMAT
}

export const useCalendarDateFormat = () => {
  const router = useRouter()
  if (router.locale === 'it') {
    return DEFAULT_ITALIAN_DATE_FORMAT
  }

  return undefined
}

export const useTableShowTotal = () => {
  const { t } = useTranslation('common')

  const showTotal = (total: number, range: [number, number]) =>
    t('{{rangeStart}}-{{rangeEnd}} of {{total}} items', {
      rangeStart: range[0],
      rangeEnd: range[1],
      total,
    })

  return showTotal
}

export const useShouldAllowBulkActions = () => {
  const isReadOnly = useIsReadOnly()
  const isOwnerView = useIsOwnerView()
  const currentUserSettings = useLoggedInUser().settings

  return isOwnerView && !isReadOnly && (currentUserSettings?.enable_multi_row_selection ?? true)
}

export const useHasPermission = (permission: AnyCompanyPermissionType): boolean => {
  const currentUserCompanyPermissions = useLoggedInUserCompanyPermissions()
  const currentUserFacilityPermissions = useLoggedInUserFacilityPermissions()
  const enforceCompanySettings = useCurrentUserCompanySetting('enforce_company_roles') ?? false

  return hasPermission({
    permission,
    companyPermissions: currentUserCompanyPermissions,
    facilityPermissions: currentUserFacilityPermissions,
    enforceCompanyRoles: enforceCompanySettings,
  })
}

export const useHasPermissionOrIsSuperAdmin = (permission: AnyCompanyPermissionType): boolean => {
  const currentUser = useLoggedInUser()
  const hasPerm = useHasPermission(permission)

  return currentUser.isSuperAdmin || hasPerm
}

export const useCurrentUserDepartmentFilterOptions = () => {
  const currentUser = useLoggedInUser()
  const companyFacilityDepartmentsQuery = useQueryCompanyFacilityDepartments(
    currentUser.company_id,
    currentUser.currentFacility,
    {}
  )

  const departmentFilterOptions =
    companyFacilityDepartmentsQuery.data?.results.map((department) => ({
      text: department.name,
      value: department._id,
    })) ?? []

  return departmentFilterOptions
}

export const usePersistentTableFilters = <T,>({
  schema,
  localStorageKey,
  update,
}: {
  schema: ZodSchema<T>
  localStorageKey: TableStateFilter
  update: (parsed: T) => void
}) => {
  const currentUser = useLoggedInUser()

  useEffect(() => {
    const raw = localStorage.getItem(localStorageKey)
    if (!raw) return

    try {
      const filters = JSON.parse(raw)
      const parsedFilters = schema.parse(filters)

      update(parsedFilters)
    } catch (err) {
      const errorMessage = retrieveErrorMessage(err)
      logException(
        new Error('Failed to parse filters from localStorage: ' + errorMessage),
        currentUser
      )
    }
  }, [localStorageKey, schema])
}

export const usePrint = ({
  onAfterPrint,
  pageStyle = 'body {}',
}: {
  onAfterPrint?: () => void
  pageStyle?: string
}) => {
  const printRef = useRef<HTMLDivElement>(null)
  const resolveRef = useRef<(() => void) | null>(null)
  const [shouldMountPrint, setShouldMountPrint] = useState(false)

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle,
    onBeforePrint: () => {
      return new Promise<void>((resolve) => {
        resolveRef.current = () => {
          resolve()
        }
        setShouldMountPrint(true)
      })
    },
    onAfterPrint: () => {
      onAfterPrint?.()
      setShouldMountPrint(false)
      resolveRef.current = null
    },
  })

  return { handlePrint, printRef, resolveRef, shouldMountPrint }
}
